import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Chip,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Add, Edit, Delete, Warning, Search } from '@mui/icons-material';
import { ModuleLayout } from '../../components/Layout/ModuleLayout';
import PendingApprovalsSection from '../../components/common/PendingApprovalsSection';
import apiClient from '../../api/client';
import { moduleService, modulePenaltyService, ModulePenalty } from '../../services';
import { usePermission } from '../../hooks';
import { colors } from '../../theme/theme';

export const ModulePenaltiesPage = () => {
  const { id } = useParams<{ id: string }>();
  const { can } = usePermission();
  const [module, setModule] = useState<any>(null);
  const [penalties, setPenalties] = useState<any[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [billingCycles, setBillingCycles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPenalty, setSelectedPenalty] = useState<any>(null);
  const [formData, setFormData] = useState({
    service_id: '',
    billing_cycle_id: '',
    type: '',
    frequency: '',
    amount: '',
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const getTypeLabel = (type: number) => {
    return type === 1 ? 'Fixed' : type === 2 ? 'Percentage' : 'N/A';
  };

  const filteredPenalties = penalties.filter((penalty) => {
    const query = searchQuery.toLowerCase();
    return (
      penalty.service?.name?.toLowerCase().includes(query) ||
      getTypeLabel(penalty.type).toLowerCase().includes(query) ||
      penalty.amount?.toString().includes(query)
    );
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [moduleRes, penaltiesRes, servicesRes, cyclesRes] = await Promise.all([
        moduleService.getModule(Number(id)),
        modulePenaltyService.getAll(Number(id!)),
        apiClient.get(`/admin/county/modules/${id}/services`),
        apiClient.get(`/admin/county/billing-cycles`),
      ]);

      if (moduleRes.status === 'success') {
        setModule(moduleRes.data);
      }
      if (penaltiesRes.status === 'success' && penaltiesRes.data) {
        const raw = penaltiesRes.data as any;
        const penaltiesData = Array.isArray(raw) ? raw : (raw?.data ?? []);
        setPenalties(Array.isArray(penaltiesData) ? penaltiesData : []);
      }
      if (servicesRes.status === 'success') {
        const servicesData = servicesRes.data?.data || servicesRes.data || [];
        setServices(Array.isArray(servicesData) ? servicesData : []);
      }
      if (cyclesRes.status === 'success') {
        const cyclesData = cyclesRes.data?.data || cyclesRes.data || [];
        setBillingCycles(Array.isArray(cyclesData) ? cyclesData : []);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (penalty?: any) => {
    if (penalty) {
      setSelectedPenalty(penalty);
      setFormData({
        service_id: penalty.service_id?.toString() || '',
        billing_cycle_id: penalty.billing_cycle_id?.toString() || '',
        type: penalty.type?.toString() || '',
        frequency: penalty.frequency?.toString() || '',
        amount: penalty.amount?.toString() || '',
      });
    } else {
      setSelectedPenalty(null);
      setFormData({ service_id: '', billing_cycle_id: '', type: '', frequency: '', amount: '' });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPenalty(null);
    setFormData({ service_id: '', billing_cycle_id: '', type: '', frequency: '', amount: '' });
  };

  const handleSubmit = async () => {
    try {
      const data = {
        service_id: formData.service_id ? parseInt(formData.service_id) : undefined,
        billing_cycle_id: formData.billing_cycle_id ? parseInt(formData.billing_cycle_id) : undefined,
        type: formData.type ? parseInt(formData.type) : undefined,
        frequency: formData.frequency ? parseInt(formData.frequency) : undefined,
        amount: formData.amount ? parseFloat(formData.amount) : 0,
      };

      let response;
      if (selectedPenalty) {
        response = await modulePenaltyService.update(Number(id!), selectedPenalty.id, data);
        if (response.status === 'success') {
          setSnackbar({
            open: true,
            message: response.message || 'Update request submitted for approval',
            severity: 'success',
          });
          setTabValue(1);
        }
      } else {
        response = await modulePenaltyService.create(Number(id!), data);
        if (response.status === 'success') {
          setSnackbar({
            open: true,
            message: 'Penalty created successfully',
            severity: 'success',
          });
        }
      }
      fetchData();
      handleCloseDialog();
    } catch (err: any) {
      console.error('Failed to save penalty:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.errors || 'Failed to save penalty';
      setSnackbar({
        open: true,
        message: typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage),
        severity: 'error',
      });
    }
  };

  const handleDelete = async (penaltyId: number) => {
    if (!window.confirm('Are you sure you want to submit a delete request for this penalty?')) return;
    try {
      const response = await modulePenaltyService.delete(Number(id!), penaltyId);
      if (response.status === 'success') {
        setSnackbar({
          open: true,
          message: response.message || 'Delete request submitted for approval',
          severity: 'success',
        });
        setTabValue(1);
        fetchData();
      }
    } catch (err: any) {
      console.error('Failed to delete penalty:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to delete penalty',
        severity: 'error',
      });
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getFrequencyLabel = (frequency: number) => {
    return frequency === 1 ? 'Once' : frequency === 2 ? 'Recurring' : 'N/A';
  };

  const columns: GridColDef[] = [
    {
      field: 'service',
      headerName: 'Service',
      width: 200,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {params.row.service?.name || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'billingCycle',
      headerName: 'Billing Cycle',
      width: 180,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.row.billing_cycle?.name || params.row.billingCycle?.name || 'N/A'}
          size="small"
          sx={{ fontWeight: 600, bgcolor: '#eff6ff', color: '#1e40af' }}
        />
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 150,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={getTypeLabel(params.row.type)}
          size="small"
          sx={{ fontWeight: 600, bgcolor: '#fef3c7', color: '#92400e' }}
        />
      ),
    },
    {
      field: 'frequency',
      headerName: 'Frequency',
      width: 150,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={getFrequencyLabel(params.row.frequency)}
          size="small"
          sx={{ fontWeight: 600, bgcolor: '#f3e8ff', color: '#6b21a8' }}
        />
      ),
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 180,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ fontWeight: 600, color: '#dc2626' }}>
          {formatCurrency(params.row.amount)}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.row.status || 'Active'}
          color="success"
          size="small"
          sx={{ fontWeight: 600, textTransform: 'capitalize' }}
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      display: 'flex',
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          {can('penalties.edit') && (
            <IconButton
              size="small"
              onClick={() => handleOpenDialog(params.row)}
              sx={{ color: colors.primary.main }}
            >
              <Edit fontSize="small" />
            </IconButton>
          )}
          {can('penalties.delete') && (
            <IconButton
              size="small"
              onClick={() => handleDelete(params.row.id)}
              sx={{ color: '#dc2626' }}
            >
              <Delete fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <ModuleLayout module={module} moduleId={id!}>
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      </ModuleLayout>
    );
  }

  return (
    <ModuleLayout module={module} moduleId={id!}>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1f2937', mb: 1 }}>
              Penalties
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Manage late payment penalties and fines
            </Typography>
          </Box>
          {can('penalties.create') && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{
                background: colors.gradients.primary,
                boxShadow: colors.shadows.button,
                '&:hover': {
                  background: colors.gradients.primaryHover,
                  boxShadow: colors.shadows.buttonHover,
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Add Penalty
            </Button>
          )}
        </Box>

        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Penalties" />
          <Tab label="Approvals" />
        </Tabs>

        {tabValue === 0 && (
          <>
            <TextField
              fullWidth
              placeholder="Search penalties by service name, type, or amount..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
              }}
              sx={{ mb: 3 }}
            />

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Card sx={{ borderRadius: 4 }}>
              <CardContent>
                <DataGrid
                  rows={filteredPenalties}
                  columns={columns}
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  pageSizeOptions={[5, 10, 25, 50]}
                  disableRowSelectionOnClick
                  autoHeight
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-cell:focus': {
                      outline: 'none',
                    },
                    '& .MuiDataGrid-row:hover': {
                      bgcolor: '#f9fafb',
                    },
                  }}
                  slots={{
                    noRowsOverlay: () => (
                      <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Box
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            bgcolor: colors.primary[50],
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 3,
                          }}
                        >
                          <Warning sx={{ fontSize: 40, color: colors.primary.main }} />
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                          {searchQuery ? 'No Matching Penalties' : 'No Penalties Found'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                          {searchQuery ? 'Try adjusting your search terms' : 'Create your first penalty rule'}
                        </Typography>
                        {!searchQuery && can('penalties.create') && (
                          <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => handleOpenDialog()}
                            sx={{
                              background: colors.gradients.primary,
                            }}
                          >
                            Add Penalty
                          </Button>
                        )}
                      </Box>
                    ),
                  }}
                />
          </CardContent>
        </Card>
          </>
        )}

        {tabValue === 1 && (
          <PendingApprovalsSection
            entityType="penalty"
            title="Pending Penalty Approvals"
            onApprovalComplete={fetchData}
          />
        )}

        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{selectedPenalty ? 'Edit Penalty' : 'Add Penalty'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <FormControl fullWidth required>
                <InputLabel>Service</InputLabel>
                <Select
                  value={formData.service_id}
                  label="Service"
                  onChange={(e) => setFormData({ ...formData, service_id: e.target.value as string })}
                >
                  <MenuItem value="">
                    <em>Select Service</em>
                  </MenuItem>
                  {services.map((service) => {
                    const hasPenalty = penalties.some(
                      (p) => p.service_id === service.id && (!selectedPenalty || selectedPenalty.service_id !== service.id)
                    );
                    return (
                      <MenuItem key={service.id} value={service.id.toString()} disabled={hasPenalty}>
                        {service.name}{hasPenalty ? ' (penalty exists)' : ''}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <FormControl fullWidth required>
                <InputLabel>Billing Cycle</InputLabel>
                <Select
                  value={formData.billing_cycle_id}
                  label="Billing Cycle"
                  onChange={(e) => setFormData({ ...formData, billing_cycle_id: e.target.value as string })}
                >
                  <MenuItem value="">
                    <em>Select Billing Cycle</em>
                  </MenuItem>
                  {billingCycles.map((cycle) => (
                    <MenuItem key={cycle.id} value={cycle.id.toString()}>
                      {cycle.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth required>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type}
                  label="Type"
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as string })}
                >
                  <MenuItem value="">
                    <em>Select Type</em>
                  </MenuItem>
                  <MenuItem value="1">Fixed</MenuItem>
                  <MenuItem value="2">Percentage</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth required>
                <InputLabel>Frequency</InputLabel>
                <Select
                  value={formData.frequency}
                  label="Frequency"
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value as string })}
                >
                  <MenuItem value="">
                    <em>Select Frequency</em>
                  </MenuItem>
                  <MenuItem value="1">Once</MenuItem>
                  <MenuItem value="2">Recurring</MenuItem>
                </Select>
              </FormControl>
              <TextField
                fullWidth
                required
                label={formData.type === '2' ? 'Percentage (%)' : 'Amount (KES)'}
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                InputProps={{
                  inputProps: { min: 0, max: formData.type === '2' ? 100 : undefined, step: 0.01 }
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              sx={{
                background: colors.gradients.primary,
              }}
            >
              {selectedPenalty ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ModuleLayout>
  );
};
